import React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { formatPeriod } from '@/lib/utils'
import { FORM_ERROR_TEXT_CLASS, FORM_INVALID_FIELD_CLASS } from '@/components/ui/Tone'

interface MonthSelectorProps {
  month: string
  onPrevious: () => void
  onNext: () => void
  canGoNext?: boolean
}

export function MonthSelector({ month, onPrevious, onNext, canGoNext = true }: MonthSelectorProps) {
  const monthName = new Intl.DateTimeFormat('ru-RU', { 
    month: 'long', 
    year: 'numeric' 
  }).format(new Date(month + '-01'))

  const formatMonthTitle = (name: string) => {
    // Capitalize only the first word (month) and keep the rest (year, "г.") intact
    const parts = name.split(' ')
    if (!parts.length) return name
    const [first, ...rest] = parts
    const capitalizedFirst = first.charAt(0).toUpperCase() + first.slice(1)
    return [capitalizedFirst, ...rest].join(' ')
  }

  return (
    <div className="flex items-center justify-between py-4">
      <button 
        onClick={onPrevious}
        className="p-2 hover:bg-muted rounded-lg transition-colors"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      
      <h2 className="text-xl font-semibold">
        {formatMonthTitle(monthName)}
      </h2>
      
      <button 
        onClick={onNext}
        disabled={!canGoNext}
        className="p-2 hover:bg-muted rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  )
}

interface PeriodSelectorProps {
  period: {
    period_start: string
    period_end: string
    month_number: number
    year_number: number
  }
  onPrevious: () => void
  onNext: () => void
  canGoNext?: boolean
}

export function PeriodSelector({ 
  period, 
  onPrevious, 
  onNext, 
  canGoNext = true 
}: PeriodSelectorProps) {
  const periodTitle = formatPeriod(period)

  return (
    <div className="flex items-center justify-between py-3 sm:py-4 gap-2">
      <button 
        onClick={onPrevious}
        className="p-2 hover:bg-muted rounded-lg transition-colors flex-shrink-0"
      >
        <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
      </button>
      
      <div className="text-center flex-1 min-w-0">
        <h2 className="text-sm sm:text-base lg:text-lg font-semibold px-2">
          {periodTitle}
        </h2>
      </div>
      
      <button 
        onClick={onNext}
        disabled={!canGoNext}
        className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
          canGoNext
            ? 'hover:bg-muted'
            : 'invisible pointer-events-none'
        }`}
      >
        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
      </button>
    </div>
  )
}

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: React.ReactNode
  error?: string
}

export function Input({ label, error, className = '', ...props }: InputProps) {
  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-sm font-medium text-foreground">
          {label}
        </label>
      )}
      <input
        className={`input w-full ${error ? 'border-error' : ''} ${className}`}
        {...props}
      />
      {error && (
        <p className="text-sm text-error">{error}</p>
      )}
    </div>
  )
}

interface NumberInputProps extends Omit<InputProps, 'type' | 'onChange'> {
  value?: number
  onChange?: (value: number | undefined) => void
  inputMode?: 'numeric' | 'decimal'
  onFocus?: () => void
  onBlur?: () => void
  maxIntegerDigits?: number
  invalidInputMessage?: string
  onInvalidInput?: (message: string) => void
}

export function NumberInput({
  value,
  onChange,
  inputMode = 'decimal',
  onFocus,
  onBlur,
  maxIntegerDigits,
  invalidInputMessage,
  onInvalidInput,
  className = '',
  ...props
}: NumberInputProps) {
  const [displayValue, setDisplayValue] = React.useState<string>('')
  const [isEditing, setIsEditing] = React.useState(false)
  const [formulaError, setFormulaError] = React.useState<string>('')
  const [showInvalidState, setShowInvalidState] = React.useState(false)
  const pendingLocalValueRef = React.useRef<number | undefined | null>(null)
  const invalidStateTimeoutRef = React.useRef<number | null>(null)

  const normalizeDecimalSeparator = (raw: string) => raw.replace(/,/g, '.')
  const roundToTwoDecimals = (num: number) => Math.round((num + Number.EPSILON) * 100) / 100
  const parseBoundary = (boundary: unknown): number | undefined => {
    if (typeof boundary === 'number' && Number.isFinite(boundary)) return boundary
    if (typeof boundary === 'string' && boundary.trim() !== '') {
      const parsed = Number(boundary)
      return Number.isFinite(parsed) ? parsed : undefined
    }
    return undefined
  }
  const minValue = parseBoundary(props.min)
  const maxValue = parseBoundary(props.max)
  const clampToBounds = (num: number) => {
    let next = roundToTwoDecimals(num)
    if (minValue !== undefined) next = Math.max(minValue, next)
    if (maxValue !== undefined) next = Math.min(maxValue, next)
    return roundToTwoDecimals(next)
  }
  const exceedsMaxIntegerDigits = (num: number) => {
    if (!Number.isFinite(num) || maxIntegerDigits === undefined) return false
    const integerPart = Math.trunc(Math.abs(num))
    return integerPart.toString().length > maxIntegerDigits
  }
  const exceedsMaxIntegerDigitsInRawInput = (raw: string) => {
    if (maxIntegerDigits === undefined || raw.startsWith('=')) return false
    const normalized = normalizeDecimalSeparator(raw).replace(/\s/g, '')
    const unsignedValue = normalized.replace(/^[+-]/, '')
    const integerPart = unsignedValue.split('.')[0] ?? ''
    return integerPart.length > maxIntegerDigits
  }
  const notifyInvalidInput = () => {
    if (invalidStateTimeoutRef.current !== null) {
      window.clearTimeout(invalidStateTimeoutRef.current)
    }
    setShowInvalidState(true)
    invalidStateTimeoutRef.current = window.setTimeout(() => {
      setShowInvalidState(false)
      invalidStateTimeoutRef.current = null
    }, 1200)
    onInvalidInput?.(
      invalidInputMessage || `Можно ввести не больше ${maxIntegerDigits} цифр`
    )
  }
  const formatDisplayNumber = (num: number) => {
    const rounded = roundToTwoDecimals(num)
    if (Number.isInteger(rounded)) return String(rounded)
    return rounded.toFixed(2).replace(/\.?0+$/, '')
  }

  // Синхронизация displayValue с prop value когда не редактируем
  React.useEffect(() => {
    const normalizedValue = value !== undefined && value !== null ? roundToTwoDecimals(value) : undefined
    const formattedValue = value !== undefined && value !== null ? formatDisplayNumber(value) : ''

    if (!isEditing) {
      setDisplayValue(formattedValue)
      pendingLocalValueRef.current = null
      return
    }

    // Если значение пришло не из локального ввода, это внешнее обновление формы:
    // сценарий, переключение недели, загрузка плана. В этом случае показываем новое значение сразу.
    if (pendingLocalValueRef.current !== normalizedValue) {
      setDisplayValue(formattedValue)
      setIsEditing(false)
      pendingLocalValueRef.current = null
    }
  }, [value, isEditing])

  React.useEffect(() => {
    return () => {
      if (invalidStateTimeoutRef.current !== null) {
        window.clearTimeout(invalidStateTimeoutRef.current)
      }
    }
  }, [])

  const evaluateFormula = (formula: string): number | null => {
    try {
      // Удаляем знак "=" в начале
      const expression = normalizeDecimalSeparator(formula.slice(1).trim())
      
      // Проверяем, что выражение содержит только разрешенные символы
      if (!/^[\d+\-*/().\s]+$/.test(expression)) {
        throw new Error('Недопустимые символы')
      }
      
      // Вычисляем выражение
      // eslint-disable-next-line no-eval
      const result = eval(expression)
      
      if (typeof result !== 'number' || isNaN(result) || !isFinite(result)) {
        throw new Error('Некорректный результат')
      }
      
      return roundToTwoDecimals(result)
    } catch (error) {
      return null
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setFormulaError('')

    if (exceedsMaxIntegerDigitsInRawInput(val)) {
      notifyInvalidInput()
      return
    }

    setDisplayValue(val)
    
    if (val === '') {
      pendingLocalValueRef.current = undefined
      onChange?.(undefined)
    } else if (val.startsWith('=')) {
      // Не вычисляем формулу во время ввода, только сохраняем в displayValue
      // Вычисление произойдет при потере фокуса
    } else {
      const num = parseFloat(normalizeDecimalSeparator(val))
      if (!isNaN(num)) {
        if (exceedsMaxIntegerDigits(num)) {
          notifyInvalidInput()
          return
        }
        const bounded = clampToBounds(num)
        pendingLocalValueRef.current = bounded
        onChange?.(bounded)
        if (bounded !== roundToTwoDecimals(num)) {
          setDisplayValue(formatDisplayNumber(bounded))
        }
      }
    }
  }

  const handleFocusLocal = () => {
    setIsEditing(true)
    onFocus?.()
  }

  const handleBlurLocal = () => {
    setIsEditing(false)
    
    // Если введена формула, вычисляем её
    if (displayValue.startsWith('=')) {
      const result = evaluateFormula(displayValue)
      if (result !== null) {
        if (exceedsMaxIntegerDigits(result)) {
          notifyInvalidInput()
          setDisplayValue(value !== undefined && value !== null ? formatDisplayNumber(value) : '')
          pendingLocalValueRef.current = null
          onBlur?.()
          return
        }
        const bounded = clampToBounds(result)
        pendingLocalValueRef.current = bounded
        onChange?.(bounded)
        setDisplayValue(formatDisplayNumber(bounded))
        setFormulaError('')
      } else {
        setFormulaError('Ошибка в формуле')
        // Возвращаем к предыдущему значению
        setDisplayValue(value !== undefined && value !== null ? formatDisplayNumber(value) : '')
      }
    }
    
    pendingLocalValueRef.current = null
    onBlur?.()
  }

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    // Предотвращаем изменение значения при прокрутке колесиком
    e.currentTarget.blur()
  }

  return (
    <div className="space-y-1">
      <Input
        type="text"
        inputMode={inputMode}
        value={displayValue}
        onChange={handleChange}
        onWheel={handleWheel}
        onFocus={handleFocusLocal}
        onBlur={handleBlurLocal}
        className={`${className} ${showInvalidState ? FORM_INVALID_FIELD_CLASS : ''}`.trim()}
        {...props}
      />
      {formulaError && (
        <div className={`text-xs ${FORM_ERROR_TEXT_CLASS}`}>{formulaError}</div>
      )}
      {isEditing && displayValue.startsWith('=') && (
        <div className="text-xs text-muted-foreground">
          💡 Формула будет вычислена после завершения ввода
        </div>
      )}
    </div>
  )
}

interface SelectProps {
  label?: string
  value: string
  onChange: (value: string) => void
  options: Array<{ value: string; label: string }>
  error?: string
  className?: string
}

export function Select({ label, value, onChange, options, error, className = '' }: SelectProps) {
  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-sm font-medium text-foreground">
          {label}
        </label>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`input w-full ${error ? 'border-error' : ''} ${className}`}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <p className="text-sm text-error">{error}</p>
      )}
    </div>
  )
}
